import * as React from 'react'
import type { CSSProperties } from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@/lib/utils'

export const Tabs = TabsPrimitive.Root

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, style, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    style={{ ...styles.list, ...style }}
    className={className}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, style, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    style={{ ...styles.trigger, ...style }}
    className={cn('tabs-trigger', className)}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, style, children, ...props }, ref) => {
  // Radix applies a `hidden` attribute on inactive tab panels. If the caller
  // passes `display: 'flex'` (or any display value) via inline style, that
  // inline display overrides the browser's `[hidden] { display: none }` rule,
  // causing ALL tab panels to be visible and compete for flex space.
  //
  // To fix this we split the styles:
  //   • Outer (Radix element): only flex-item sizing (flex, minHeight, overflow,
  //     margin) — no `display` override so `[hidden]` works properly.
  //   • Inner wrapper: carries `display`, `flexDirection`, and `height: 100%`
  //     so the active panel's content lays out correctly.
  const {
    display: innerDisplay,
    flexDirection: innerFlexDirection,
    ...outerStyle
  } = { ...styles.content, ...style } as CSSProperties & Record<string, unknown>

  // Only add the inner wrapper if a display override was requested
  const needsInnerWrapper = innerDisplay != null

  return (
    <TabsPrimitive.Content
      ref={ref}
      style={outerStyle}
      className={className}
      {...props}
    >
      {needsInnerWrapper ? (
        <div style={{
          display: innerDisplay,
          flexDirection: innerFlexDirection,
          flex: 1,
          minHeight: 0,
          height: '100%',
        }}>
          {children}
        </div>
      ) : (
        children
      )}
    </TabsPrimitive.Content>
  )
})
TabsContent.displayName = TabsPrimitive.Content.displayName

const styles: Record<string, CSSProperties> = {
  list: {
    display: 'inline-flex',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)',
    padding: '4px',
    color: 'var(--text-muted)',
    gap: '2px',
  },
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all var(--transition-fast)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'inherit',
    outline: 'none',
    textDecoration: 'none',
    height: '30px',
  },
  content: {
    marginTop: '8px',
    outline: 'none',
  },
}
